/**
 * Despia Native SDK Utilities
 * Provides native capabilities for web-native mobile apps
 */

// Lazy-loaded despia SDK - initialized on first use to prevent module-level crashes
let despiaImport: ((command: string, watch?: string[]) => Promise<any>) | null = null;
let despiaInitialized = false;
let despiaInitPromise: Promise<void> | null = null;

/**
 * Check if we're likely in a Despia native environment based on user agent
 */
function detectNativeEnvironment(): boolean {
  try {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent.toLowerCase();
    // Check for explicit Despia marker OR iOS WKWebView (no Safari version string)
    return ua.includes('despia') || 
      ((/iphone|ipad|ipod/.test(ua)) && !ua.includes('safari/') && !ua.includes('crios/') && !ua.includes('fxios/'));
  } catch {
    return false;
  }
}

/**
 * Lazily initialize the despia SDK on first use.
 * This prevents module-level crashes on web/non-native platforms.
 * Uses async dynamic import for better ESM compatibility.
 */
function initDespia(): typeof despiaImport {
  if (despiaInitialized) {
    return despiaImport;
  }
  
  despiaInitialized = true;
  
  // Only attempt import if we detect we might be in a native environment
  if (!detectNativeEnvironment()) {
    return null;
  }
  
  try {
    // Wrap require in a function call to prevent Vite from analyzing it at build time
    // This is critical for preventing module-level crashes
    const globalObj = globalThis as any;
    const requireFn = typeof globalObj.require === 'function' ? globalObj.require : null;
    if (requireFn) {
      const moduleName = 'despia-native';
      const despiaNative = requireFn(moduleName);
      const sdk = despiaNative?.default || despiaNative;
      if (typeof sdk === 'function') {
        despiaImport = sdk;
      }
    }
  } catch (e) {
    // Silently fail - this is expected on web platforms or if SDK not available
    console.warn('[Despia] SDK not available:', e instanceof Error ? e.message : 'unknown error');
    despiaImport = null;
  }
  
  return despiaImport;
}

/**
 * Async initialization for environments that need dynamic import
 */
async function initDespiaAsync(): Promise<typeof despiaImport> {
  if (despiaInitialized) {
    return despiaImport;
  }
  
  if (despiaInitPromise) {
    await despiaInitPromise;
    return despiaImport;
  }
  
  despiaInitPromise = (async () => {
    despiaInitialized = true;
    
    if (!detectNativeEnvironment()) {
      return;
    }
    
    try {
      // Try dynamic import first (ESM compatible)
      const despiaNative = await import('despia-native');
      const sdk = despiaNative?.default || despiaNative;
      if (typeof sdk === 'function') {
        despiaImport = sdk;
      }
    } catch {
      // Fall back to require if dynamic import fails
      try {
        const globalObj = globalThis as any;
        const requireFn = typeof globalObj.require === 'function' ? globalObj.require : null;
        if (requireFn) {
          const despiaNative = requireFn('despia-native');
          const sdk = despiaNative?.default || despiaNative;
          if (typeof sdk === 'function') {
            despiaImport = sdk;
          }
        }
      } catch (e) {
        console.warn('[Despia] SDK not available');
        despiaImport = null;
      }
    }
  })();
  
  await despiaInitPromise;
  return despiaImport;
}

/**
 * Safe wrapper around despia to handle errors gracefully.
 * Prevents app crashes if despia-native throws on non-native platforms (iOS Safari, web).
 */
export function safeDespia(command: string, watch?: string[]): Promise<any> {
  const despia = initDespia();
  if (!despia) {
    return Promise.resolve();
  }
  try {
    return despia(command, watch);
  } catch (e) {
    console.error('[Despia] Command failed:', e);
    return Promise.resolve();
  }
}

// IAP Success callback data from Despia native runtime
export interface IAPSuccessData {
  planID: string;
  transactionID: string;
  subreceipts?: any;
}

declare global {
  interface Window {
    bioauthSuccess?: () => void;
    bioauthFailure?: () => void;
    bioauthUnavailable?: () => void;
    keychainGetSuccess?: (value: string) => void;
    keychainGetFailure?: () => void;
    keychainSetSuccess?: () => void;
    keychainSetFailure?: () => void;
    notificationPermissionGranted?: () => void;
    notificationPermissionDenied?: () => void;
    // IAP callbacks
    iapSuccess?: (data: IAPSuccessData) => void;
    iapFailure?: (error: string) => void;
    iapCancelled?: () => void;
  }
}

/**
 * Check if running in Despia native environment
 */
export function isDespiaNative(): boolean {
  try {
    return typeof navigator !== 'undefined' && 
           navigator.userAgent.toLowerCase().includes('despia');
  } catch {
    return false;
  }
}

// ========================================
// HAPTIC FEEDBACK
// ========================================

export type HapticType = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

const hapticUrls: Record<HapticType, string> = {
  light: 'lighthaptic://',
  medium: 'mediumhaptic://',
  heavy: 'heavyhaptic://',
  success: 'successhaptic://',
  warning: 'warninghaptic://',
  error: 'errorhaptic://',
};

/**
 * Trigger haptic feedback
 */
export function triggerHaptic(type: HapticType = 'light'): void {
  if (!isDespiaNative()) return;
  
  const url = hapticUrls[type];
  if (url) {
    safeDespia(url);
  }
}

// ========================================
// NATIVE SETTINGS
// ========================================

/**
 * Open the app's native settings page (iOS Settings app)
 * Deep-links to the app's settings in iOS Settings or Android Settings
 */
export function openNativeSettings(): void {
  console.log('[Despia] Opening native settings...');
  
  // Try the Despia SDK protocol first
  const despia = initDespia();
  if (despia) {
    try {
      // Try opensettings:// protocol
      despia('opensettings://');
      console.log('[Despia] Triggered opensettings:// protocol');
      return;
    } catch (e) {
      console.warn('[Despia] opensettings:// failed:', e);
    }
  }
  
  // Try WebKit message handler for WKWebView
  if (typeof window !== 'undefined') {
    const webkit = (window as any).webkit;
    if (webkit?.messageHandlers?.openSettings) {
      try {
        webkit.messageHandlers.openSettings.postMessage({});
        console.log('[Despia] Triggered WebKit openSettings');
        return;
      } catch (e) {
        console.warn('[Despia] WebKit openSettings failed:', e);
      }
    }
  }
  
  // Fallback: Try iframe with app-prefs: URL scheme (iOS deep link to settings)
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    // app-prefs:root=Privacy&path=HEALTH opens Health privacy settings on iOS
    iframe.src = 'app-prefs:root=Privacy&path=HEALTH';
    document.body.appendChild(iframe);
    
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 1000);
    console.log('[Despia] Triggered app-prefs:// fallback');
  } catch (e) {
    console.error('[Despia] All settings methods failed:', e);
  }
}

// ========================================
// NATIVE PUSH NOTIFICATION PERMISSION
// ========================================

/**
 * Request push notification permission on native iOS/Android via Despia protocol
 * Uses 'registerpush://' which is the correct Despia protocol for push registration
 */
export function requestNativeNotificationPermission(): Promise<boolean> {
  return new Promise((resolve) => {
    if (!isDespiaNative()) {
      console.warn('[Despia] Not in native environment for notification permission');
      resolve(false);
      return;
    }
    
    console.log('[Despia] Requesting native notification permission via registerpush://');
    
    // Use the correct Despia protocol for push notification registration
    // This triggers the native iOS/Android permission dialog and registers with push service
    safeDespia('registerpush://');
    
    // The registerpush:// protocol handles everything natively (permission + registration)
    // We resolve true to indicate the request was triggered successfully
    // The actual permission result is handled by the native runtime
    resolve(true);
  });
}

// ========================================
// NATIVE SOCIAL SHARING
// ========================================

interface ShareOptions {
  message: string;
  url?: string;
}

/**
 * Trigger native social sharing dialog
 */
export function nativeShare({ message, url }: ShareOptions): void {
  if (!isDespiaNative()) {
    // Fallback to Web Share API
    if (navigator.share) {
      navigator.share({
        text: message,
        url: url,
      }).catch(() => {
        // User cancelled or share failed
      });
    }
    return;
  }
  
  // Build Despia share URL
  let shareUrl = `share://${encodeURIComponent(message)}`;
  if (url) {
    shareUrl += `?url=${encodeURIComponent(url)}`;
  }
  
  safeDespia(shareUrl);
}

// ========================================
// SECURE KEYCHAIN STORAGE
// ========================================

interface KeychainResult {
  success: boolean;
  value?: string;
}

/**
 * Store a value securely in the device keychain
 */
export function keychainSet(key: string, value: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (!isDespiaNative()) {
      // Fallback to localStorage (less secure)
      try {
        localStorage.setItem(`secure_${key}`, btoa(value));
        resolve(true);
      } catch {
        resolve(false);
      }
      return;
    }

    window.keychainSetSuccess = () => {
      console.log('[Despia] Keychain set success');
      resolve(true);
    };

    window.keychainSetFailure = () => {
      console.log('[Despia] Keychain set failure');
      resolve(false);
    };

    // Set timeout in case callbacks don't fire
    const timeout = setTimeout(() => {
      resolve(false);
    }, 5000);

    const originalSuccess = window.keychainSetSuccess;
    window.keychainSetSuccess = () => {
      clearTimeout(timeout);
      originalSuccess?.();
    };

    const originalFailure = window.keychainSetFailure;
    window.keychainSetFailure = () => {
      clearTimeout(timeout);
      originalFailure?.();
    };

    safeDespia(`keychain://set?key=${encodeURIComponent(key)}&value=${encodeURIComponent(value)}`);
  });
}

/**
 * Retrieve a value from the device keychain
 */
export function keychainGet(key: string): Promise<KeychainResult> {
  return new Promise((resolve) => {
    if (!isDespiaNative()) {
      // Fallback to localStorage
      try {
        const stored = localStorage.getItem(`secure_${key}`);
        if (stored) {
          resolve({ success: true, value: atob(stored) });
        } else {
          resolve({ success: false });
        }
      } catch {
        resolve({ success: false });
      }
      return;
    }

    window.keychainGetSuccess = (value: string) => {
      console.log('[Despia] Keychain get success');
      resolve({ success: true, value });
    };

    window.keychainGetFailure = () => {
      console.log('[Despia] Keychain get failure');
      resolve({ success: false });
    };

    // Set timeout in case callbacks don't fire
    const timeout = setTimeout(() => {
      resolve({ success: false });
    }, 5000);

    const originalSuccess = window.keychainGetSuccess;
    window.keychainGetSuccess = (value: string) => {
      clearTimeout(timeout);
      originalSuccess?.(value);
    };

    const originalFailure = window.keychainGetFailure;
    window.keychainGetFailure = () => {
      clearTimeout(timeout);
      originalFailure?.();
    };

    safeDespia(`keychain://get?key=${encodeURIComponent(key)}`);
  });
}

/**
 * Remove a value from the keychain
 */
export function keychainRemove(key: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (!isDespiaNative()) {
      try {
        localStorage.removeItem(`secure_${key}`);
        resolve(true);
      } catch {
        resolve(false);
      }
      return;
    }

    // Use set with empty value to remove
    window.keychainSetSuccess = () => {
      resolve(true);
    };

    window.keychainSetFailure = () => {
      resolve(false);
    };

    safeDespia(`keychain://delete?key=${encodeURIComponent(key)}`);
  });
}

// ========================================
// CREDENTIAL STORAGE FOR BIOMETRIC AUTH
// ========================================

const CREDENTIAL_KEY = 'evolve_user_credentials';

interface StoredCredentials {
  email: string;
  password: string;
}

/**
 * Store user credentials securely for biometric login
 */
export async function storeCredentials(email: string, password: string): Promise<boolean> {
  const credentials: StoredCredentials = { email, password };
  return keychainSet(CREDENTIAL_KEY, JSON.stringify(credentials));
}

/**
 * Retrieve stored credentials after biometric verification
 */
export async function getStoredCredentials(): Promise<StoredCredentials | null> {
  const result = await keychainGet(CREDENTIAL_KEY);
  if (result.success && result.value) {
    try {
      return JSON.parse(result.value) as StoredCredentials;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Clear stored credentials (on logout or disable biometric)
 */
export async function clearStoredCredentials(): Promise<boolean> {
  return keychainRemove(CREDENTIAL_KEY);
}

// ========================================
// IN-APP PURCHASE VIA DESPIA PROTOCOL
// ========================================

/**
 * Trigger a Despia protocol URL
 * CRITICAL: The Despia SDK intercepts protocol URLs via its internal mechanism.
 * We try multiple methods to ensure the protocol is triggered and callbacks work:
 * 1. First try the SDK's internal assignment (safeDespia) - most reliable for callbacks
 * 2. If that fails, fall back to iframe injection
 */
function triggerDespiaProtocol(protocolUrl: string): void {
  console.log('[Despia] Triggering protocol URL:', protocolUrl);
  
  // Primary method: Use the SDK wrapper if available
  // The SDK internally uses window.webkit.messageHandlers or similar
  // which properly maintains the callback chain
  const despia = initDespia();
  if (despia) {
    console.log('[Despia] Using SDK wrapper for protocol trigger');
    try {
      // The SDK's internal mechanism - this is the proper way to trigger protocols
      // that need callbacks to fire back
      despia(protocolUrl);
      console.log('[Despia] SDK wrapper triggered successfully');
      return;
    } catch (e) {
      console.warn('[Despia] SDK wrapper failed, trying fallback:', e);
    }
  }
  
  // Fallback: Use hidden iframe (works for triggering but callbacks may not fire)
  console.log('[Despia] Using iframe fallback for protocol trigger');
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    
    // Set the src to trigger the protocol
    iframe.src = protocolUrl;
    
    // Add to DOM
    document.body.appendChild(iframe);
    console.log('[Despia] Protocol iframe added to DOM');
    
    // Keep iframe in DOM longer to allow callbacks to fire
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
        console.log('[Despia] Protocol iframe removed from DOM');
      }
    }, 5000);
  } catch (e) {
    console.error('[Despia] Failed to trigger protocol via iframe:', e);
  }
}

/**
 * Purchase a product via Despia RevenueCat protocol
 * This triggers the native iOS App Store purchase flow
 * CRITICAL: Must be called SYNCHRONOUSLY within user gesture chain
 */
export function purchaseWithDespia(userId: string, productId: string): void {
  if (!isDespiaNative()) {
    console.error('[Despia] Cannot purchase - not in native environment');
    return;
  }
  
  const protocolUrl = `revenuecat://purchase?external_id=${encodeURIComponent(userId)}&product=${encodeURIComponent(productId)}`;
  console.log('[Despia] Starting purchase via protocol', { userId, productId, protocolUrl });
  
  // Trigger the protocol SYNCHRONOUSLY using SDK - no async/await before this!
  triggerDespiaProtocol(protocolUrl);
}

/**
 * Restore previous purchases via Despia protocol
 */
export function restoreDespiaPurchases(): void {
  if (!isDespiaNative()) {
    console.error('[Despia] Cannot restore - not in native environment');
    return;
  }
  
  console.log('[Despia] Restoring purchases via protocol');
  triggerDespiaProtocol('restoreinapppurchases://');
}

/**
 * Setup IAP success callback from Despia native runtime
 * The native runtime will call window.iapSuccess when purchase completes
 */
export function setupIAPSuccessCallback(
  onSuccess: (data: IAPSuccessData) => void,
  onFailure?: (error: string) => void,
  onCancelled?: () => void
): () => void {
  console.log('[Despia] Setting up IAP callbacks');
  
  window.iapSuccess = (data: IAPSuccessData) => {
    console.log('[Despia] IAP Success callback received:', data);
    onSuccess(data);
  };
  
  window.iapFailure = (error: string) => {
    console.log('[Despia] IAP Failure callback received:', error);
    onFailure?.(error);
  };
  
  window.iapCancelled = () => {
    console.log('[Despia] IAP Cancelled callback received');
    onCancelled?.();
  };
  
  // Return cleanup function
  return () => {
    window.iapSuccess = undefined;
    window.iapFailure = undefined;
    window.iapCancelled = undefined;
  };
}

// ========================================
// iOS PERMISSION HANDLING
// ========================================

export type PermissionType = 'camera' | 'microphone' | 'photos';

export interface PermissionResult {
  granted: boolean;
  canRequest: boolean;
}

// Global callbacks for permission requests
declare global {
  interface Window {
    cameraPermissionGranted?: () => void;
    cameraPermissionDenied?: () => void;
    microphonePermissionGranted?: () => void;
    microphonePermissionDenied?: () => void;
    photoLibraryPermissionGranted?: () => void;
    photoLibraryPermissionDenied?: () => void;
  }
}

/**
 * Check and request camera permission on iOS via Despia
 * Falls back to navigator.mediaDevices on web
 */
export async function requestCameraPermission(): Promise<PermissionResult> {
  console.log('[Despia] Requesting camera permission');
  
  if (!isDespiaNative()) {
    // Web fallback - try to request via navigator
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Stop the stream immediately, we just needed permission
      stream.getTracks().forEach(track => track.stop());
      console.log('[Despia] Camera permission granted (web)');
      return { granted: true, canRequest: true };
    } catch (error) {
      console.log('[Despia] Camera permission denied (web)', error);
      return { granted: false, canRequest: true };
    }
  }
  
  // Native iOS - use Despia protocol
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      console.log('[Despia] Camera permission timeout');
      resolve({ granted: false, canRequest: false });
    }, 10000);
    
    window.cameraPermissionGranted = () => {
      clearTimeout(timeout);
      console.log('[Despia] Camera permission granted (native)');
      resolve({ granted: true, canRequest: true });
    };
    
    window.cameraPermissionDenied = () => {
      clearTimeout(timeout);
      console.log('[Despia] Camera permission denied (native)');
      resolve({ granted: false, canRequest: false });
    };
    
    // Request camera permission via Despia
    safeDespia('requestcamerapermission://');
  });
}

/**
 * Check and request microphone permission on iOS via Despia
 * Falls back to navigator.mediaDevices on web
 */
export async function requestMicrophonePermission(): Promise<PermissionResult> {
  console.log('[Despia] Requesting microphone permission');
  
  if (!isDespiaNative()) {
    // Web fallback
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      console.log('[Despia] Microphone permission granted (web)');
      return { granted: true, canRequest: true };
    } catch (error) {
      console.log('[Despia] Microphone permission denied (web)', error);
      return { granted: false, canRequest: true };
    }
  }
  
  // Native iOS
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      console.log('[Despia] Microphone permission timeout');
      resolve({ granted: false, canRequest: false });
    }, 10000);
    
    window.microphonePermissionGranted = () => {
      clearTimeout(timeout);
      console.log('[Despia] Microphone permission granted (native)');
      resolve({ granted: true, canRequest: true });
    };
    
    window.microphonePermissionDenied = () => {
      clearTimeout(timeout);
      console.log('[Despia] Microphone permission denied (native)');
      resolve({ granted: false, canRequest: false });
    };
    
    safeDespia('requestmicrophonepermission://');
  });
}

/**
 * Check and request photo library permission on iOS via Despia
 * On web, this is not needed as the file picker handles it
 */
export async function requestPhotoLibraryPermission(): Promise<PermissionResult> {
  console.log('[Despia] Requesting photo library permission');
  
  if (!isDespiaNative()) {
    // Web doesn't require photo library permission for file picker
    console.log('[Despia] Photo library permission not required (web)');
    return { granted: true, canRequest: true };
  }
  
  // Native iOS
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      console.log('[Despia] Photo library permission timeout');
      resolve({ granted: false, canRequest: false });
    }, 10000);
    
    window.photoLibraryPermissionGranted = () => {
      clearTimeout(timeout);
      console.log('[Despia] Photo library permission granted (native)');
      resolve({ granted: true, canRequest: true });
    };
    
    window.photoLibraryPermissionDenied = () => {
      clearTimeout(timeout);
      console.log('[Despia] Photo library permission denied (native)');
      resolve({ granted: false, canRequest: false });
    };
    
    safeDespia('requestphotolibrary://');
  });
}

/**
 * Open iOS Settings app for permission management
 */
export function openIOSSettings(): void {
  if (!isDespiaNative()) {
    console.log('[Despia] Cannot open iOS Settings - not in native environment');
    return;
  }
  
  console.log('[Despia] Opening iOS Settings');
  safeDespia('opensettings://');
}
